import { record } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { isEmpty, isFunction, transform } from 'lodash';
import { get, gt, isEqual, isNaN, isNil, isUndefined, last, split, toPath } from 'lodash/fp';
import validatejs from 'validate.js';

import {
    MAX_ACCOUNT_NOTE_INPUT_LENGTH,
    MAX_LEGAL_IDENTIFIER_INPUT_LENGTH,
    MAX_LONG_INPUT_TEXT_LENGTH,
    MAX_TINY_INPUT_TEXT_LENGTH,
} from '@mews-commander/constants';
import { getContainer } from '@mews-commander/legacy-app-instance';
import {
    getDateFromDateTimeUtc,
    localizedDateStringToDate,
    localizeWallClockDateString,
    notEmpty,
    reduceIndexed,
    reduceRightIndexed,
    transformIndexed,
} from '@mews-commander/utils';
import { ServiceTimeClass, TimeUnit } from '@mews/enums';
import { compactArray } from '@mews/fp';
import {
    addPaymentValidators,
    DateService,
    DateTimePart,
    DateTimeService,
    DateTimeUtcService,
    DurationService,
    DurationUtils,
    LocalizedTextProps,
    MAX_INPUT_TEXT_LENGTH,
    MIN_BIRTH_DATE_STRING,
    NumberService,
    TimeService,
    WallClockDateService,
} from '@mews/framework';

import { TextKey } from '@mews-commander/enums';

import { minMaxValidator as minMaxFieldValidator } from './fieldLevelValidators';
import { SelectFieldOptions, ValidationRules } from './types';
import { isValidCombination, validateDateRange } from './utils';

const VALIDATION_OPTIONS = { fullMessages: false, format: 'single' };

addPaymentValidators(validatejs);

export * from './processError';
export * from './types';
export * from './utils';

export type Constraints<K extends PropertyKey> = Partial<Record<K, ValidationRules>>;

/**
 * @deprecated use validateWithZod for redux-form
 * or createZodFormResolver for hook form
 */
export const validation = (getConstraints: $TSFixMe) => (values: $TSFixMe, props: $TSFixMe) => {
    const constraints = isFunction(getConstraints) ? getConstraints(props, values) : getConstraints;
    const validated = validatejs(values, constraints, VALIDATION_OPTIONS);
    if (isUndefined(validated)) {
        // It's anticipated that validatejs returns 'undefined' as a valid value
        return validated;
    }
    return reduceIndexed(
        // @ts-expect-error $TSFixMeError: Parameter 'acc' implicitly has an 'any' type.
        (acc, value, key) => ({
            ...acc,

            ...reduceRightIndexed(
                // @ts-expect-error $TSFixMeError: Parameter 'ac' implicitly has an 'any' type.
                (ac, pathKey) => ({
                    [pathKey]: ac,
                }),
                value,
                toPath(key)
            ),
        }),
        {},
        validated
    );
};

export const validateSingle = (value: $TSFixMe, constraints: $TSFixMe) => {
    const errors = validatejs.single(value, constraints);
    if (errors) {
        return errors[0];
    }

    return errors;
};

export const createValidateSingle = (constraints: $TSFixMe) => (value: $TSFixMe) => validateSingle(value, constraints);

export const presence = { message: TextKey.ValidateRequired };
export const email = { message: TextKey.ValidateEmail };
export const optionalEmail = { message: TextKey.ValidateEmail };
export const dateString = { message: TextKey.ValidateDate };
export const dateRange = { message: TextKey.InvalidServiceOrderEndErrorMessage };
export const withinDateTimeRange = { message: TextKey.ValidateDateTimeRange };
export const birthDateRange = { message: TextKey.BirthDateInvalidErrorMessage };
export const timeString = { message: TextKey.ValidateTime };
export const timeRangeBasedOnTimeUnit = { message: TextKey.ValidateTime };
export const url = { message: TextKey.InvalidUrlErrorMessage };
export const optionalUrl = { message: TextKey.InvalidUrlErrorMessage };
export const formattedNumber = { message: TextKey.ValidateFormattedNumber };
export const formattedCurrency = { message: TextKey.ValidateFormattedNumber };
export const nonZeroAmount = { message: TextKey.ChargingZeroAmountErrorMessage };
export const nonZeroCurrencyValue = { message: TextKey.ChargingZeroAmountErrorMessage };
export const positiveAmount = { message: TextKey.ValidatePositiveNumber };

export const maxTextLength = (maximum: number, textKey = TextKey.MaximumTextLengthErrorMessage) => ({
    message: { textKey, substitutes: { maximum } },
    maximum,
});

export const maxInputTextLength = maxTextLength(MAX_INPUT_TEXT_LENGTH);
export const maxLegalIdentifierInputTextLength = maxTextLength(MAX_LEGAL_IDENTIFIER_INPUT_LENGTH);
export const maxTinyInputTextLength = maxTextLength(MAX_TINY_INPUT_TEXT_LENGTH);
export const maxLongInputTextLength = maxTextLength(MAX_LONG_INPUT_TEXT_LENGTH);
export const maxAccountNoteInputLength = maxTextLength(MAX_ACCOUNT_NOTE_INPUT_LENGTH);

export const minMaxValues = ({ minFieldName }: $TSFixMe) => ({
    message: TextKey.RestrictionMaxLessThanMinErrorMessage,
    minFieldName,
});
export const upperBoundNumber = ({ upperBound, cultureInfo }: $TSFixMe) => ({
    message: TextKey.ValidateNumberUpperBound,
    upperBound,
    cultureInfo,
});
export const minMaxNumber = (min: number, max: number, message?: Mews.TextKey | LocalizedTextProps) => ({
    message,
    min,
    max,
});
const numberNotGreaterThanOrEqualToErrorMessageHandler = (number: $TSFixMe) => ({
    textKey: TextKey.NumberNotGreaterThanOrEqualToErrorMessage,
    substitutes: { number },
});

export const nonZeroRequiredCurrencyValue = {
    formattedCurrency,
    nonZeroCurrencyValue,
    presence,
};

export const customPriceValidation = {
    formattedCurrency,
    positiveAmount,
    presence,
};

export const integerGreaterOrEqualTo = (threshold: $TSFixMe) => ({
    onlyInteger: true,
    strict: true,
    greaterThanOrEqualTo: threshold,
    message: numberNotGreaterThanOrEqualToErrorMessageHandler(threshold),
});

export const integerGreaterOrEqualToZero = integerGreaterOrEqualTo(0);
export const integerGreaterOrEqualToOne = integerGreaterOrEqualTo(1);

export const eitherArrayNotEmpty = ({ otherFieldName, message }: $TSFixMe) => ({
    otherFieldName,
    message,
});

(validatejs as $TSFixMe).formatters.single = (errors: $TSFixMe) =>
    transform(
        errors,
        (acc, e) => {
            if (!acc[(e as $TSFixMe).attribute]) {
                acc[(e as $TSFixMe).attribute] = (e as $TSFixMe).error;
            }
        },
        {}
    );

const upperBoundNumberValidator = (value: $TSFixMe, { message, upperBound, cultureInfo }: $TSFixMe) =>
    NumberService.unformat(value, cultureInfo) > upperBound
        ? {
              textKey: message,
              substitutes: { upperBound: NumberService.format(upperBound, cultureInfo) },
          }
        : null;

const createAmountValidator =
    (comparator: $TSFixMe) =>
    (value: $TSFixMe, { message }: $TSFixMe) => {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        const parsedNumber = appLocalizationComponent.parseNumber(value);
        return comparator(parsedNumber) ? message : null;
    };

const createCurrencyValueValidator =
    (comparator: $TSFixMe) =>
    (value: $TSFixMe, { message }: $TSFixMe) => {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        const parsedNumber = appLocalizationComponent.parseCurrencyValue(value);
        return comparator(parsedNumber) ? message : null;
    };

const greaterThanCurrencyValueValidator = (value: $TSFixMe, { threshold }: $TSFixMe) => {
    const container = getContainer();
    const {
        appLocalizationComponent: { parseCurrencyValue },
    } = container();
    const parsedCurrencyValue = parseCurrencyValue(value);
    const parsedComparisonCurrencyValue = parseCurrencyValue(threshold);
    return parsedCurrencyValue <= parsedComparisonCurrencyValue ? TextKey.AmountMustBeGreaterThanOriginalAmount : null;
};

const lowerThanCurrencyValueValidator = (value: $TSFixMe, { threshold }: $TSFixMe) => {
    const container = getContainer();
    const {
        appLocalizationComponent: { parseCurrencyValue },
    } = container();
    const parsedCurrencyValue = parseCurrencyValue(value);
    const parsedComparisonCurrencyValue = parseCurrencyValue(threshold);
    return parsedCurrencyValue > parsedComparisonCurrencyValue ? TextKey.AmountMustBeLowerThanOriginalAmount : null;
};

const nonZeroAmountValidator = createAmountValidator(isEqual(0));
const nonZeroCurrencyValueValidator = createCurrencyValueValidator(isEqual(0));

const positiveAmountValidator = createAmountValidator(gt(0));

const minMaxValuesValidator = (
    maxValue: $TSFixMe,
    { minFieldName, message }: $TSFixMe,
    fieldName: $TSFixMe,
    formValues: $TSFixMe
) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    const minValue = formValues[minFieldName];

    if (!isNil(maxValue) && !isNil(minValue)) {
        const maxNumber = appLocalizationComponent.parseNumber(maxValue);
        const minNumber = appLocalizationComponent.parseNumber(minValue);

        if (!isNaN(maxNumber) && !isNaN(minNumber) && minNumber > maxNumber) {
            return message;
        }
    }

    return null;
};

const dateRangeLessThanPeriodValidator = (
    value: string | null | undefined,
    {
        message,
        restrictedRange,
        dateTimePart,
        startDateFieldName,
    }: { message: Mews.TextKey; restrictedRange: number; startDateFieldName: string; dateTimePart: DateTimePart },
    fieldName: string,
    formValues: { [startDateFieldName: string]: string | null | undefined }
) => {
    const startDateValue = formValues[startDateFieldName];
    if (value && startDateValue && areDateRangesValidStrings({ startDate: startDateValue, endDate: value })) {
        const { startDate, endDate } = localizedDateRangeToDates({ startDate: startDateValue, endDate: value });
        return DateService.diff(dateTimePart, { roundDown: false })(endDate, startDate) > restrictedRange
            ? message
            : null;
    }
};

const trueValueValidator = (value: $TSFixMe, options: $TSFixMe) => (value !== true ? options.message : null);

const optionalEmailValidator = (value: $TSFixMe, options: $TSFixMe) =>
    !isEmpty(value)
        ? (validatejs as $TSFixMe).validators.email.call((validatejs as $TSFixMe).validators.email, value, options)
        : null;

const optionalExpirationValidator = (value: $TSFixMe, options: $TSFixMe) =>
    !isEmpty(value)
        ? (validatejs as $TSFixMe).validators.expiration.call(
              (validatejs as $TSFixMe).validators.expiration,
              value,
              options
          )
        : null;

const optionalUrlValidator = (value: $TSFixMe, options: $TSFixMe) =>
    !isEmpty(value)
        ? (validatejs as $TSFixMe).validators.url.call((validatejs as $TSFixMe).validators.url, value, options)
        : null;

const dateStringValidator = (value: $TSFixMe, options: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return value &&
        !WallClockDateService.isValidString(value, appLocalizationComponent.getCurrentDateTimeLocalizationInfo())
        ? options.message
        : null;
};

const areDateRangesValidStrings = ({ startDate, endDate }: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    const dateTimeLocalizationInfo = appLocalizationComponent.getCurrentDateTimeLocalizationInfo();
    return (
        WallClockDateService.isValidString(startDate, dateTimeLocalizationInfo) &&
        WallClockDateService.isValidString(endDate, dateTimeLocalizationInfo)
    );
};

const localizedDateRangeToDates = ({ startDate, endDate }: $TSFixMe) => ({
    startDate: localizedDateStringToDate(startDate),
    endDate: localizedDateStringToDate(endDate),
});

const dateRangeValidator = (value: $TSFixMe, { message, ...other }: $TSFixMe) => {
    if (!isNil(other) && areDateRangesValidStrings(other)) {
        const { startDate, endDate } = localizedDateRangeToDates(other);
        return DateService.isLess(endDate, startDate) ? message : null;
    }
};

const withinDateTimeRangeValidator = (
    value: $TSFixMe,
    { message, startUtc, endUtc, dateStr, timeStr, timezone, key }: $TSFixMe
) => {
    if (value && dateStr && timeStr) {
        const container = getContainer();
        const { appLocalizationComponent } = container();
        const localization = appLocalizationComponent.getCurrentDateTimeLocalizationInfo();

        if (
            !WallClockDateService.isValidString(dateStr, localization) ||
            !TimeService.isValidShortString(timeStr, localization) ||
            (key === 'date' && !WallClockDateService.isValidString(value, localization)) ||
            (key === 'time' && !TimeService.isValidShortString(value, localization))
        ) {
            return null;
        }

        const date = DateService.fromString(key === 'date' ? value : dateStr, localization, timezone);
        const startDate = getDateFromDateTimeUtc(startUtc, timezone);
        const endDate = getDateFromDateTimeUtc(endUtc, timezone);

        if (key === 'date' && (isEqual(date, startDate) || isEqual(date, endDate))) {
            return null;
        }

        const time = TimeService.fromShortString(key === 'time' ? value : timeStr, localization);
        const dateTime = DateTimeService.fromDateAndTime(date, time);
        const dateTimeUtc = DateTimeUtcService.fromEpoch(dateTime.millis);
        return DateTimeUtcService.isLess(dateTimeUtc, endUtc) && DateTimeUtcService.isGreater(dateTimeUtc, startUtc)
            ? null
            : message;
    }
    return null;
};

const localizeTimeStringToTime = (time: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return appLocalizationComponent.parseShortTime(time);
};

const areTimeRangesValidStrings = ({ arrivalTime, departureTime }: $TSFixMe) =>
    arrivalTime &&
    departureTime &&
    !(
        timeStringValidator(arrivalTime, { message: 'ValidateTime' }) ||
        timeStringValidator(departureTime, { message: 'ValidateTime' })
    );

const localizeTimeRangeToTimes = ({ arrivalTime, departureTime }: $TSFixMe) => ({
    arrivalTime: localizeTimeStringToTime(arrivalTime),
    departureTime: localizeTimeStringToTime(departureTime),
});

const ARRIVAL_TIME_KEY = 'arrivalTime';
const DEPARTURE_TIME_KEY = 'departureTime';

const timeRangeBasedOnTimeUnitValidator = (value: $TSFixMe, props: $TSFixMe, key: $TSFixMe, attributes: $TSFixMe) => {
    const { bookablePeriod } = attributes;
    if (value && bookablePeriod && areTimeRangesValidStrings(attributes)) {
        const { arrivalTime, departureTime } = localizeTimeRangeToTimes(attributes);
        if (bookablePeriod === ServiceTimeClass.Night && TimeService.isGreaterOrEqual(departureTime, arrivalTime)) {
            if (key === ARRIVAL_TIME_KEY) {
                return TextKey.ArrivalTimeLessThanDepartureTimeErrorMessage;
            }
            if (key === DEPARTURE_TIME_KEY) {
                return TextKey.DepartureTimeGreaterThanArrivalErrorMessage;
            }
        }
        if (bookablePeriod === ServiceTimeClass.Day && TimeService.isLessOrEqual(departureTime, arrivalTime)) {
            if (key === ARRIVAL_TIME_KEY) {
                return TextKey.ArrivalTimeGreaterThanDepartureTimeErrorMessage;
            }
            if (key === DEPARTURE_TIME_KEY) {
                return TextKey.DepartureTimeLessThanArrivalErrorMessage;
            }
        }
    }
    return null;
};

const dateNotInPastValidator = (value: $TSFixMe, { message }: $TSFixMe) => {
    const container = getContainer();
    const { appDateComponent, appLocalizationComponent } = container();
    const dateTimeLocalizationInfo = appLocalizationComponent.getCurrentDateTimeLocalizationInfo();

    if (!isNil(value) && WallClockDateService.isValidString(value, dateTimeLocalizationInfo)) {
        const selectedDate = appLocalizationComponent.parseDate(value);
        const todayDate = appDateComponent.today();
        return DateTimeService.isLess(selectedDate, todayDate) ? message : null;
    }
};

const dateTimeNotInPastValidator = (value: $TSFixMe, { message }: $TSFixMe, fieldName: $TSFixMe, values: $TSFixMe) => {
    let date;
    let time;

    const container = getContainer();
    const { appLocalizationComponent, appDateComponent } = container();
    const dateTimeLocalizationInfo = appLocalizationComponent.getCurrentDateTimeLocalizationInfo();

    const isDate = WallClockDateService.isValidString(value, dateTimeLocalizationInfo);
    const secondField = isDate ? 'time' : 'date';

    if (isDate) {
        date = value;
        time = get(secondField, values);
    } else {
        time = value;
        date = get(secondField, values);
    }

    if (isDate) {
        return dateNotInPastValidator(date, { message });
    }

    const isTimeValid = TimeService.isValidShortString(time, dateTimeLocalizationInfo);
    const isDateValid = WallClockDateService.isValidString(date, dateTimeLocalizationInfo);

    if (isTimeValid && isDateValid) {
        const selectedDate = appLocalizationComponent.parseDate(date);
        const now = appDateComponent.now();
        const isDateLessOrEqual = DateTimeService.isLessOrEqual(selectedDate, now);

        const parsedTime = localizeTimeStringToTime(time);
        const parsedNow = localizeTimeStringToTime(appLocalizationComponent.localizeShortTime(now));
        const isLess = isDateLessOrEqual ? TimeService.isLess(parsedTime, parsedNow) : false;
        return isLess ? message : null;
    }
    return null;
};

const minMaxNumberValidator = (value: string, { message, min, max }: ReturnType<typeof minMaxNumber>) =>
    minMaxFieldValidator(min, max, message)(value);

const birthDateRangeValidator = (value: $TSFixMe, options: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    if (
        !isEmpty(value) &&
        WallClockDateService.isValidString(value, appLocalizationComponent.getCurrentDateTimeLocalizationInfo())
    ) {
        const { value: parsedValue } = appLocalizationComponent.parseWallClockDate(value);
        const { value: today } = WallClockDateService.today();
        const isValidDateRange = validateDateRange(MIN_BIRTH_DATE_STRING, today)(parsedValue);
        const localizedMinBirthDate = localizeWallClockDateString(MIN_BIRTH_DATE_STRING);
        return isValidDateRange
            ? null
            : { textKey: options.message, substitutes: { minBirthDate: localizedMinBirthDate } };
    }
};

const timeStringValidator = (value: $TSFixMe, options: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();

    return value &&
        !TimeService.isValidShortString(value, appLocalizationComponent.getCurrentDateTimeLocalizationInfo())
        ? options.message
        : null;
};

const passwordPolicyValidator = (value: $TSFixMe, { message }: $TSFixMe) =>
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/.test(value) ? null : message;

const pciProxyValueValidator = (__: $TSFixMe, { metadata: { valid = false, length = 0 } = {}, message }: $TSFixMe) =>
    length > 0 && valid ? null : message;
const pciProxyCardTypeValidator = (__: $TSFixMe, { metadata: { types = [], type = null } = {}, message }: $TSFixMe) =>
    type && types.every((t: $TSFixMe) => t.toLowerCase() !== type.toLowerCase()) ? message(type) : null;

const getFieldArrayErrorMessage = flow(split(' '), last);
const transformFieldArrayError = transformIndexed((acc: $TSFixMe, value: $TSFixMe, index: $TSFixMe) => {
    const isArrayString = typeof value[0] === 'string';
    acc[index] = isArrayString ? getFieldArrayErrorMessage(value) : last(value);
}, {});
const fieldArrayValidator = (values = [], constraints: $TSFixMe) => {
    const errors = values.reduce((acc, item, index) => {
        const error = validatejs(item, constraints);
        if (error) {
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{}'.
            acc[index] = transformFieldArrayError(error);
        }
        return acc;
    }, {});
    return errors;
};

const uniqueFieldsArrayValidator = (values = [], constraints: $TSFixMe) => {
    const errors = values.reduce((acc, item, index) => {
        const key = record.keys(item)[0];
        const constraint = { [key]: constraints[key] };
        const error = validatejs(item, constraint);
        if (error) {
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{}'.
            acc[index] = transformFieldArrayError(error);
        }
        return acc;
    }, {});
    return errors;
};

const eitherArrayNotEmptyValidator = (
    value: $TSFixMe,
    { otherFieldName, message }: $TSFixMe,
    fieldName: $TSFixMe,
    formValues: $TSFixMe
) => {
    const otherValue = formValues[otherFieldName];
    if (value?.filter(notEmpty).length === 0 && otherValue?.filter(notEmpty).length === 0) {
        return message;
    }
    return null;
};

const dateRangeRestrictionDeleteFormValidator = (
    dateValue: string | null | undefined,
    { message }: { message: Mews.TextKey },
    fieldName: string,
    formValues: { days?: string[] | null }
) => (!dateValue && (formValues.days?.length ?? 0) < 7 ? message : null);

const emailsValidator = (value: string[] = []) => {
    const invalidEmails = compactArray(value.map((item) => (validatejs.single(item, { email }) ? item : null)));
    if (invalidEmails.length > 0) {
        return {
            invalidEmails,
            textKey: TextKey.MultipleEmailsValidationWarning,
            substitutes: { emails: invalidEmails.join(', ') },
        };
    }
    return null;
};

const caseSensitiveExclusionValdiator = (value: string, options: { within: Array<string>; message: Mews.TextKey }) => {
    const lowerCaseWithin = options.within.map((w) => w.toLowerCase());
    return lowerCaseWithin.includes(value.toLowerCase()) ? { textKey: options.message } : null;
};

type FieldValue = string | number | boolean | null | undefined;

const forbiddenValueCombinationValidator = (
    value: FieldValue | FieldValue[],
    {
        fieldValues,
        otherFieldName,
        otherFieldValues,
        message,
    }: {
        fieldValues: FieldValue[];
        otherFieldName: string;
        otherFieldValues: FieldValue[];
        message: Mews.TextKey;
    },
    fieldName: string,
    formValues: Record<string, FieldValue | FieldValue[]>
) => {
    const otherValue = formValues[otherFieldName];

    const isFieldValueMatch = fieldValues.some((fieldValue) =>
        Array.isArray(value) ? value.includes(fieldValue) : value === fieldValue
    );

    const isOtherFieldValueMatch = otherFieldValues.some((otherFieldValue) =>
        Array.isArray(otherFieldValue) ? otherFieldValue.includes(otherValue) : otherValue === otherFieldValue
    );

    if (isFieldValueMatch && isOtherFieldValueMatch) {
        return message;
    }

    return null;
};

const valueCombinationsValidator = (
    value: string[] = [],
    {
        additionalValueName,
        validCombinations,
        options,
        message,
    }: {
        additionalValueName: string;
        validCombinations: string[][];
        options?: SelectFieldOptions;
        message: Mews.TextKey;
    }
) => {
    const selectedValues = options
        ? options
              .filter((option) => value.includes(option.value))
              .map((option) => option.additionalValues?.find((val) => val.propertyName === additionalValueName)?.value)
              .filter((item, index, self): item is string => item != null && self.indexOf(item) === index)
        : [];

    return isValidCombination({ validCombinations, selectedValues }) ? null : message;
};
interface TimeUnitField {
    fieldName: string;
    timeUnit: TimeUnit;
}

const getDurationFromFields = (fields: TimeUnitField[], formValues: Record<string, FieldValue | FieldValue[]>) => {
    const durationObj = fields.reduce((acc, { fieldName, timeUnit }: TimeUnitField) => {
        const fieldValue = formValues[fieldName];
        if (!timeUnit || fieldValue == null || Number.isNaN(Number(fieldValue))) return acc;
        const durationKey = DurationUtils.timeUnitToDurationObjectKey(timeUnit);
        return { ...acc, [durationKey]: fieldValue };
    }, {});
    return Object.keys(durationObj).length ? DurationService.asDays(DurationService.fromObject(durationObj)) : null;
};
const combinedMinMaxLengthValidator = (
    value: FieldValue | FieldValue[],
    { min, max }: { min: TimeUnitField[]; max: TimeUnitField[] },
    fieldName: string,
    formValues: Record<string, FieldValue | FieldValue[]>
) => {
    const minDurationInMinutes = getDurationFromFields(min, formValues) ?? 0;
    const maxDuratonInMinuites = getDurationFromFields(max, formValues);
    if (maxDuratonInMinuites == null) return null; // no max means nothing to validate
    return maxDuratonInMinuites < minDurationInMinutes ? TextKey.RestrictionMaxLessThanMinErrorMessage : null;
};

validatejs.validators = {
    ...validatejs.validators,
    dateString: dateStringValidator,
    dateRange: dateRangeValidator,
    dateRangeLessThanPeriod: dateRangeLessThanPeriodValidator,
    withinDateTimeRange: withinDateTimeRangeValidator,
    timeRangeBasedOnTimeUnit: timeRangeBasedOnTimeUnitValidator,
    birthDateRange: birthDateRangeValidator,
    timeString: timeStringValidator,
    optionalEmail: optionalEmailValidator,
    optionalExpiration: optionalExpirationValidator,
    optionalUrl: optionalUrlValidator,
    eitherArrayNotEmpty: eitherArrayNotEmptyValidator,
    minMaxNumber: minMaxNumberValidator,
    formattedNumber: () => {
        throw new Error('formattedNumber validator is not initialized.');
    },
    formattedCurrency: () => {
        throw new Error('formattedCurrency validator is not initialized.');
    },
    nonZeroAmount: nonZeroAmountValidator,
    nonZeroCurrencyValue: nonZeroCurrencyValueValidator,
    positiveAmount: positiveAmountValidator,
    greaterThanCurrencyValue: greaterThanCurrencyValueValidator,
    lowerThanCurrencyValue: lowerThanCurrencyValueValidator,
    passwordPolicy: passwordPolicyValidator,
    pciProxyValue: pciProxyValueValidator,
    pciProxyCardType: pciProxyCardTypeValidator,
    fieldArray: fieldArrayValidator,
    minMaxValues: minMaxValuesValidator,
    dateNotInPast: dateNotInPastValidator,
    dateTimeNotInPast: dateTimeNotInPastValidator,
    upperBoundNumber: upperBoundNumberValidator,
    trueValue: trueValueValidator,
    uniqueFieldsArray: uniqueFieldsArrayValidator,
    emails: emailsValidator,
    dateRangeRestrictionDeleteForm: dateRangeRestrictionDeleteFormValidator,
    caseSensitiveExclusion: caseSensitiveExclusionValdiator,
    forbiddenValueCombination: forbiddenValueCombinationValidator,
    valueCombinations: valueCombinationsValidator,
    combinedMinMaxLength: combinedMinMaxLengthValidator,
};
validatejs.validators.presence.options = { allowEmpty: false };

interface CustomValidators {
    formattedNumber: $TSFixMe;
    formattedCurrency: $TSFixMe;
}

export const addCustomValidators = (validators: CustomValidators) => {
    Object.assign(validatejs.validators, validators);
};
